import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import { forceCollide } from 'd3-force';
import { filterNodes, filterLinks } from '../../../../dataUtils';
// @see: https://github.com/vasturiano/react-force-graph/issues/30#issuecomment-407168131
import fromKapsule from 'react-kapsule';
// the default for force-graph was causing an error with out tests.
// importing the .js file fixes the issue
import ForceGraph2DKapsule from 'force-graph/dist/force-graph.js';
const ForceGraph2D = fromKapsule(
  ForceGraph2DKapsule,
  { methodNames: ['d3Force', 'd3ReheatSimulation', 'stopAnimation', 'centerAt', 'zoom', 'refresh'] }
);
const HEIGHT = 800;
const SIMULATION_RUNTIME = 8000; // in milliseconds

class ClusterForceGraph extends React.Component {
  constructor(props) {
    super(props);
    this.setupRef = this.setupRef.bind(this);
    this.forceGraphCharge = this.forceGraphCharge.bind(this);
    this.calculateCollideForce = this.calculateCollideForce.bind(this);
    this.forcesConfigured = false;
  }

  componentDidMount() {
    this.forcesConfigured = this.calculateCollideForce();

    /*
     * react-kapsule 2.x uses useImperativeHandle, which may not expose d3Force
     * immediately on mount. Schedule retry via requestAnimationFrame.
     */
    if (!this.forcesConfigured) {
      this.retryForceConfig();
    }
  }

  retryForceConfig() {
    if (this.forcesConfigured) {
      return;
    }
    requestAnimationFrame(() => {
      if (this.forcesConfigured) {
        return;
      }
      this.forcesConfigured = this.calculateCollideForce();
      if (!this.forcesConfigured) {
        this.retryForceConfig();
      }
    });
  }

  shouldComponentUpdate(nextProps) {
    const {
      filteredAutomationScore,
      authorNodes,
      clusterNodes,
      clusterLinks,
      width,
      selectedTopClustersByVolume,
      showSelectionOnly,
      sizeBy
    } = this.props;

    return (
      !this.forcesConfigured
      || showSelectionOnly !== nextProps.showSelectionOnly
      || width !== nextProps.width
      || (showSelectionOnly && selectedTopClustersByVolume.length !== nextProps.selectedTopClustersByVolume.length)
      || filteredAutomationScore !== nextProps.filteredAutomationScore
      || authorNodes !== nextProps.authorNodes
      || clusterNodes !== nextProps.clusterNodes
      || clusterLinks !== nextProps.clusterLinks
      || sizeBy !== nextProps.sizeBy
    );
  }

  componentDidUpdate(prevProps) {
    if (!this.forcesConfigured) {
      this.forcesConfigured = this.calculateCollideForce();
    }
    if (prevProps.clusterNodes !== this.props.clusterNodes) {
      this.forceGraphCharge();
    }
  }

  componentWillUnmount() {
    if (this.forceGraphRef?.stopAnimation) {
      this.forceGraphRef.stopAnimation();
    }
  }

  setupRef(el) {
    this.forceGraphRef = el;
  }

  refresh() {
    if (this.forceGraphRef?.refresh) {
      this.forceGraphRef.refresh();
    }
  }

  forceGraphCharge(){
    if (!this.forceGraphRef?.d3Force) {
      return;
    }
    const { authorNodes, clusterNodes } = this.props;
    const clusterBy = clusterNodes[0] && clusterNodes[0].id.split('|')[0];
    const nodeCount = get(this.forceGraphRef, 'props.graphData.nodes.length', authorNodes.length);
    // reduce repulsion if nodeCount is low so that the nodes stay closer together
    const multiplier = nodeCount < 500 ? 2 : 6;
    const chargeForce = this.forceGraphRef.d3Force('charge');
    if (!chargeForce) {
      return;
    }
    chargeForce
      .distanceMax(250)
      .strength((d) => {
        const foundCluster = Object.keys(d.targetClusters || {}).find((clusterId) => {
          return clusterId.indexOf(`${clusterBy}|`) === 0;
        });
        // nodes in a cluster get extra repulsion so that clusters are more distinct
        const repulsionMultiplier = foundCluster ? multiplier : 1;
        return d.gravity * repulsionMultiplier;
      });
  }

  calculateCollideForce() {
    if (!this.forceGraphRef?.d3Force) {
      return false;
    }
    const defaultNodeSize = 16;
    const nodeCollisionBuffer = 1;
    const collideForceStrength = 0.5;

    // Configure ManyBody and Link forces already provided by `react-force-graph`
    this.forceGraphCharge();
    const linkForce = this.forceGraphRef.d3Force('link');
    if (linkForce) {
      linkForce.strength((d) => d.strength);
    }

    // Add collision to reduce overlapping of nodes
    const collideForce = forceCollide()
      .radius((d) => (this.props.nodeVal(d) || defaultNodeSize) + nodeCollisionBuffer)
      .strength(collideForceStrength);

    this.forceGraphRef.d3Force('collide', collideForce);

    /*
     * react-kapsule 2.x uses useImperativeHandle, which may not expose d3Force
     * immediately on mount. If forces are configured late (during componentDidUpdate),
     * reheat the simulation to restart with the correct force parameters.
     */
    if (this.forceGraphRef.d3ReheatSimulation) {
      this.forceGraphRef.d3ReheatSimulation();
    }
    return true;
  }

  render() {
    const {
      width,
      clusterLinks,
      filteredAutomationScore,
      clusterNodes,
      authorNodes,
      showSelectionOnly,
      selectedTopClustersByVolume,
      nodeVal,
      linkColor,
      onNodeHover,
      nodeCanvasObject
    } = this.props;

    const selectedClusters = showSelectionOnly ? selectedTopClustersByVolume : [];

    const nodes = filterNodes({
      authorNodes,
      clusterNodes,
      filteredAutomationScore,
      selectedClusters,
    });
    const links = filterLinks(clusterLinks, nodes);
    const graphData = { nodes, links };

    return (
      <ForceGraph2D
        ref={this.setupRef}
        graphData={graphData}
        width={width}
        height={HEIGHT}
        enableNodeDrag={false}
        linkCurvature="curvature"
        cooldownTime={SIMULATION_RUNTIME}
        autoPauseRedraw={false}
        nodeRelSize={5}
        nodeVal={nodeVal}
        linkColor={linkColor}
        onNodeHover={onNodeHover}
        nodeCanvasObject={nodeCanvasObject}
      />
    );
  }
}

ClusterForceGraph.propTypes = {
  authorNodes: PropTypes.arrayOf(PropTypes.object),
  clusterNodes: PropTypes.arrayOf(PropTypes.object),
  clusterLinks: PropTypes.arrayOf(PropTypes.object),
  filteredAutomationScore: PropTypes.object,
  width: PropTypes.number,
  setGraphHover: PropTypes.func,
  selectedTopClustersByVolume: PropTypes.array,
  showSelectionOnly: PropTypes.bool,
  mouseActiveInGraph: PropTypes.bool,
  nodeVal: PropTypes.func,
  linkColor: PropTypes.func,
  onNodeHover: PropTypes.func,
  nodeCanvasObject: PropTypes.func,
  sizeBy: PropTypes.string.isRequired,
};

ClusterForceGraph.defaultProps = {
  authorNodes: [],
  clusterNodes: [],
  clusterLinks: [],
  selectedTopClustersByVolume: [],
  filteredAutomationScore: {},
};

export default ClusterForceGraph;
